import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import PremiumPage from './pages/PremiumPage';
import { Toaster } from 'react-hot-toast';
import ThemeToggle from './components/common/ThemeToggle';
import AccountStatusNotification from './components/common/AccountStatusNotification';
import AdminDailyStats from "./components/admin/AdminDailyStats";
import AdminDashboard from "./pages/admin/AdminDashboard";
import UserManagementPage from "./pages/admin/UserManagement";
import { checkTokenAndLogout } from './utils/authUtils';

const PrivateRoute = ({ children }) => {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser) return <Navigate to="/login" />;
  const allowed = ["admin", "super_admin"].includes(currentUser.role);
  return allowed ? children : <Navigate to="/dashboard" />;
};

const PublicRoute = ({ children }) => {
  const { currentUser } = useAuth();
  return currentUser ? <Navigate to="/dashboard" /> : children;
};

function AppContent() {
  const { currentUser } = useAuth();
  const isAdmin = currentUser && ["admin", "super_admin"].includes(currentUser.role);

  // Check token expiration on mount and periodically
  useEffect(() => {
    if (currentUser) {
      // Check immediately
      checkTokenAndLogout();
      
      // Check every 30 seconds
      const interval = setInterval(() => {
        checkTokenAndLogout();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">

      <Toaster position="top-right" />

      {/*<AccountStatusNotification />*/}

      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <a href={`/`}><h1 className="text-xl font-bold text-gray-800 dark:text-white">ZIZ TECH</h1></a>
          <div className="flex items-center gap-3">
            {currentUser && (
              <Link
                to="/premium"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-sm font-semibold hover:from-yellow-600 hover:to-orange-700 transition-all hover:scale-105 active:scale-95 shadow-lg"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Premium
              </Link>
            )}
            <ThemeToggle />
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-8">
        <Routes>

        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <DashboardPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/premium"
          element={
            <PrivateRoute>
              <PremiumPage />
            </PrivateRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="/admin"
          element={<Navigate to="/admin/dashboard" replace />} />

        <Route
          path="/admin/dashboard"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />

        <Route
            path="/admin/daily-stats"
            element={
                <AdminRoute>
                    <AdminDailyStats/>
                </AdminRoute>
            }
        />

            <Route
                path="/admin/users"
                element={
                    <AdminRoute>
                        <UserManagementPage/>
                    </AdminRoute>
                }
            />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
